import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/lib/constants";
import api, { formatApiError } from "@/lib/api";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { currency } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const total = subtotal;

  const placeOrder = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({
          product_id: i.product_id,
          title: i.title,
          price_usd: i.price_usd,
          quantity: i.quantity,
        })),
        total_usd: total,
        payment_method: "paypal",
      });
      setConfirmed(data);
      clear();
      toast.success("Orden creada", { description: `Ref: ${data.reference}` });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <>
        <AppHeader title="Orden confirmada" showBack showLogo={false} />
        <main className="page-scroll px-4 pt-6 text-center" data-testid="order-confirmation">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle size={40} className="text-emerald-600" />
          </div>
          <h2 className="font-heading text-2xl font-extrabold text-[#0A2A66] mt-4">¡Gracias por tu compra!</h2>
          <p className="text-slate-600 text-sm mt-2">Recibirás las instrucciones de acceso por correo.</p>
          <div className="card-clean p-4 mt-6 text-left">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Referencia</span>
              <span className="font-mono font-bold text-[#0A2A66]">{confirmed.reference}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-slate-500">Total pagado</span>
              <span className="font-bold text-[#0A2A66]">{formatPrice(confirmed.total_usd, currency)}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-slate-500">Estado</span>
              <span className="badge-cat">Pendiente</span>
            </div>
          </div>
          <button onClick={() => navigate("/")} className="navy-btn w-full mt-6 py-3.5" data-testid="back-home-btn">
            Volver al inicio
          </button>
        </main>
      </>
    );
  }

  return (
    <>
      <AppHeader title="Checkout" showBack showLogo={false} />
      <main className="page-scroll px-4 pt-3" data-testid="checkout-page">
        <div className="card-clean p-4">
          <h3 className="font-heading text-base font-bold text-[#0A2A66]">Datos del comprador</h3>
          <div className="mt-2 text-sm text-slate-600 space-y-1">
            <div><span className="text-slate-500">Nombre:</span> {user?.name}</div>
            <div><span className="text-slate-500">Email:</span> {user?.email}</div>
            <div><span className="text-slate-500">WhatsApp:</span> {user?.whatsapp}</div>
          </div>
        </div>

        <div className="card-clean p-4 mt-3">
          <h3 className="font-heading text-base font-bold text-[#0A2A66]">Resumen</h3>
          <div className="mt-2 space-y-2">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="text-slate-600 truncate mr-2">{i.title} × {i.quantity}</span>
                <span className="font-semibold text-[#0A2A66]">{formatPrice(i.price_usd * i.quantity, currency)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 my-3" />
          <div className="flex justify-between">
            <span className="font-heading font-bold text-[#0A2A66]">Total</span>
            <span className="font-heading text-lg font-extrabold text-[#0A2A66]">{formatPrice(total, currency)}</span>
          </div>
        </div>

        <div className="card-clean p-4 mt-3">
          <h3 className="font-heading text-base font-bold text-[#0A2A66]">Método de pago</h3>
          <div className="mt-3 flex items-center gap-3 p-3 rounded-xl border-2 border-[#1E5FD9] bg-[#EEF3FF]">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
              <span className="font-bold text-[#003087]">Pay<span className="text-[#009cde]">Pal</span></span>
            </div>
            <div className="text-sm">
              <div className="font-semibold text-[#0A2A66]">PayPal</div>
              <div className="text-xs text-slate-500">Serás redirigido para completar el pago.</div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-emerald-600" /> Transacción segura y encriptada
          </div>
        </div>

        <button
          onClick={placeOrder}
          disabled={loading || items.length === 0}
          data-testid="paypal-pay-btn"
          className="gold-btn w-full mt-5 py-4 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Lock size={16} /> {loading ? "Procesando..." : `Pagar ${formatPrice(total, currency)} con PayPal`}
        </button>
      </main>
    </>
  );
}
